import clsx from 'clsx';
import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';
import { useEffect, useRef, useState, type MouseEventHandler } from 'react';
import { FeatureFlags } from '../../../../constants/FeatureFlags';
import styles from './index.module.scss';

type Props = {
  imageSrc: string;
  href: string;
};

export const FooterBanner = ({ imageSrc, href }: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const key = `isClosed:${imageSrc}`;

  useEffect(() => {
    const status = localStorage.getItem(key);
    setIsClosed(status === 'true');
  }, [key]);

  useEffect(() => {
    const handleScroll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
    };

    if (!isClosed) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isClosed]);

  const handleClose: MouseEventHandler<HTMLButtonElement> = (event) => {
    localStorage.setItem(key, 'true');
    event.stopPropagation();
    setIsClosed(true);
    setIsVisible(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!FeatureFlags.SHOW_FOOTER_BANNER || isClosed) {
    return null;
  }

  const baseClassName = clsx(styles.base, isVisible ? styles['slide-in'] : styles['slide-out']);

  return (
    <div className={baseClassName}>
      <div className={styles['image-with-close-button']}>
        <button className={styles['close-button']} type="button" aria-label="close-button" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className={styles['svg-icon']}>
            <path d="m249-207-42-42 231-231-231-231 42-42 231 231 231-231 42 42-231 231 231 231-42 42-231-231-231 231Z" />
          </svg>
        </button>
        <Link className={styles.link} href={href} target="_blank" rel="noopener">
          <ExportedImage
            alt="フッターバナー画像"
            src={imageSrc}
            className={styles.img}
            width={800}
            height={200}
            loading="lazy"
          />
        </Link>
      </div>
    </div>
  );
};
